import './market.scss'
import { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import appExpose from '@app/expose'
import { useLocal } from '@/i18n/LocalContext'
import KowTitle from '../KowTitle/KowTitle'
import TimeStatus from '../TimeStatus/TimeStatus'
import { Handler } from 'mitt'
import { useStore } from '@/store'
import { getMarketData, getPreMarketData, taskClaim } from '@app/request'
import { MarketData, Task } from '@app/store/market'
import {
  $alert,
  closeLoading,
  loading,
  openOuterPage,
  rpx,
  toast,
} from '@/utils'
import { useMaxWidth } from '@/hooks/useMaxWidth'
import MarketBtn from '../MarketBtn/MarketBtn'
import TaskBtn from '../TaskBtn/TaskBtn'
import { checkPlayer } from '@/request/utils'
import ShareImage from '@/components/ShareImage/ShareImage'
import shareImg from './base64/share_img'
import logo from '@/assets/static-img/logo.png'
import { clickReport } from '@app/report'
import { getDefaultSellData } from '@app/store/sell'
import Tooltips from '@/components/Tooltips/Tooltips'
import { Tooltip } from 'react-tooltip'

const { emitter } = appExpose

const inSDK = window.inSDK

export default function Market() {
  const { $t } = useLocal()
  const { state, setState, dispatch } = useStore()
  const inviteCode = state.code
  const zoneId = state.login.userInfo.zoneId
  const playerId = state.login.userInfo.playerId
  const coin = state.marketData.coin
  const tree = state.marketData.tree
  const price = state.marketData.price
  const overage = state.marketData.overage
  const total = state.marketData.total
  const viewTasks = state.marketData.viewTasks
  const sortViewTasks = useMemo(
    () =>
      [...viewTasks].sort((a, b) => {
        if (a.status === 1 && b.status !== 1) return -1
        if (a.status !== 1 && b.status === 1) return 1

        return a.status - b.status
      }),
    [viewTasks]
  )
  const { safeAreaTop } = useMaxWidth()
  const coinStyle = useMemo(() => {
    const style = { transform: `translateY(${safeAreaTop}px)` }
    return style
  }, [safeAreaTop])
  const [shareImageModalOpen, setShareImageModalOpen] = useState(false)
  const qrcodeUrl = useMemo(() => {
    switch (zoneId) {
      case 3:
        return 'https://kingschoicesea.go.link/4NuaO'

      case 5:
        return 'https://kingschoiceea.go.link/ju6yY'

      default:
        return 'https://kingschoice.go.link/aj5k0'
    }
  }, [zoneId])

  const openSellList = useCallback(async () => {
    clickReport('random_sale')
    if (!(await checkPlayer())) return

    dispatch({ type: 'setState', data: { sellListModalOpen: true } })
    emitter.emit('updateSellListData', { refresh: false })
  }, [])

  const openSpecifiedSell = useCallback(async () => {
    clickReport('specific_sale')
    if (!(await checkPlayer())) return

    dispatch({
      type: 'setState',
      data: {
        sellModalOpen: true,
        targetPlayerId: '',
        specifiedSellData: {
          ...getDefaultSellData(),
          tree,
        },
      },
    })
  }, [tree])

  useEffect(() => {
    const _setState = (data: MarketData) => {
      dispatch({ type: 'setState', data: { marketData: data } })
    }

    const handler1: Handler<void> = async () => {
      const data = await getPreMarketData()
      _setState(data)
    }
    const handler2: Handler<void> = async () => {
      const data = await getMarketData(playerId)
      _setState(data)
    }
    emitter.on('getPreBaseData', handler1)
    emitter.on('getBaseData', handler2)

    const handler3: Handler<MittEvents['updateMarketData']> = async () => {
      loading()
      if (playerId) {
        await handler2()
      } else {
        await handler1()
      }
      closeLoading()
    }
    emitter.on('updateMarketData', handler3)

    return () => {
      emitter.off('getPreBaseData', handler1)
      emitter.off('getBaseData', handler2)
      emitter.off('updateMarketData', handler3)
    }
  }, [playerId])

  const showHelp = useCallback(() => {
    $alert($t('圣诞集市收购数提示'))
  }, [])

  const openShareDialog = async () => {
    if (!(await checkPlayer())) return

    dispatch({ type: 'setState', data: { shareDialogModalOpen: true } })
  }

  const openBookDialog = useCallback(async () => {
    clickReport('sales_records')
    if (!(await checkPlayer())) return

    dispatch({ type: 'setState', data: { bookDialogModalOpen: true } })
    emitter.emit('updateBookData')
  }, [])

  const pendingRef = useRef(false)
  const claim = useCallback(
    async (task: Task) => {
      if (!(await checkPlayer())) return

      if (!playerId) return

      if (task.status === 2) return

      if (task.status === 0) {
        if ([1, 5].includes(task.type)) {
          openOuterPage(qrcodeUrl)
        } else if (task.type === 2) {
          clickReport('shareB')
          setShareImageModalOpen(true)
        } else if (task.type === 3) {
          openSellList()
        } else if (task.type === 4) {
          openShareDialog()
        }
        return
      }

      if (pendingRef.current) return
      pendingRef.current = true

      loading()
      const res = await taskClaim({ playerId, taskId: task.id })
      if (res) {
        toast($t('领取成功'))
        emitter.emit('updateMarketData')
      }
      closeLoading()
      pendingRef.current = false
    },
    [playerId, openSellList]
  )

  return (
    <div className="market">
      <KowTitle text={$t('圣诞集市')} />
      <TimeStatus />
      <div className="coin-block">
        <Tooltips
          title={$t('庆典币')}
          content={$t(
            '圣诞礼盒货币道具，可在圣诞礼盒活动中进行消耗刷新或购买道具'
          )}
          place="right-start"
          ckey="coin"
          className="coin"
        >
          <span>{coin}</span>
        </Tooltips>
        <Tooltips
          title={$t('圣诞树')}
          content={$t('圣诞集市活动道具，可在圣诞集市活动中进行售卖')}
          place="right-start"
          ckey="cointree"
          className="coin tree"
        >
          <span>{tree}</span>
        </Tooltips>
      </div>
      <div className="title-desc">
        <div className="title-content">{$t('圣诞集市-活动宣传语')}</div>
      </div>
      <ul className="price-block">
        <li className="price">
          <span>{$t('收购价：')}</span>
          <i className="price__icon"></i>
          <span className="price__cont">{price}</span>
          <a className="price__share" onClick={openShareDialog}></a>
        </li>
        <li className="price">
          <span>{$t('可收购数量：')}</span>
          {/* <i className="price__icon"></i> */}
          <span className="price__cont">
            {overage}/{total}
          </span>
          <a className="price__help" onClick={showHelp}></a>
        </li>
        <li className="market-btn-list-placer"></li>
        <li className="book" onClick={openBookDialog}>
          <span>{$t('售卖记录')}</span>
        </li>
      </ul>
      <div className="market-btn-list">
        <MarketBtn onClick={openSellList}>{$t('市场出售')}</MarketBtn>
        <MarketBtn type={2} onClick={openSpecifiedSell}>
          {$t('指定出售')}
        </MarketBtn>
      </div>
      <div className="main-placer">
        <div className="task-list-block">
          <div className="task-list-hints">
            {$t('任务进度更新大约有5分钟延迟，请耐心等待')}
          </div>
          <ul className="task-list">
            {sortViewTasks.map((task) => (
              <li key={task.id} className="task-item">
                <span className="task-item__name">{task.name}</span>
                <div
                  className="task-prop celebration"
                  data-tooltip-html={`<div class="prop-name">${$t(
                    '庆典值'
                  )}</div><div class="prop-desc">${$t(
                    '圣诞庆典档位进度值'
                  )}</div>`}
                  data-tooltip-id="task-tooltip"
                >
                  <span className="task-prop__num">{task.celebration}</span>
                </div>
                {/* <Tooltips
                  title={$t('庆典值')}
                  content={$t('圣诞庆典档位进度值')}
                  ckey={`celebration${task.id}`}
                  className="task-prop celebration"
                >
                  <span className="task-prop__num">{task.celebration}</span>
                </Tooltips> */}
                <div
                  className="task-prop tree"
                  data-tooltip-html={`<div class="prop-name">${$t(
                    '圣诞树'
                  )}</div><div class="prop-desc">${$t(
                    '圣诞集市活动道具，可在圣诞集市活动中进行售卖'
                  )}</div>`}
                  data-tooltip-id="task-tooltip"
                >
                  <span className="task-prop__num">{task.tree}</span>
                </div>
                {/* <Tooltips
                  title={$t('圣诞树')}
                  content={$t('圣诞集市活动道具，可在圣诞集市活动中进行售卖')}
                  ckey={`tree${task.id}`}
                  className="task-prop tree"
                >
                  <span className="task-prop__num">{task.tree}</span>
                </Tooltips> */}
                <div className="task-progress-block">
                  <span className="task-progress">
                    {task.progress}/{task.condition}
                  </span>
                  <TaskBtn type={task.status + 1} onClick={() => claim(task)}>
                    {task.statusText}
                  </TaskBtn>
                </div>
              </li>
            ))}
          </ul>
          <Tooltip
            id="task-tooltip"
            style={{
              zIndex: '4',
              padding: rpx(10),
              maxWidth: rpx(384),
              background: 'rgba(0, 0, 0, 0.8)',
            }}
            globalCloseEvents={{ scroll: true }}
          />
        </div>
      </div>
      <ShareImage
        code={inviteCode}
        qrcodeUrl={qrcodeUrl}
        open={shareImageModalOpen}
        onClose={() => setShareImageModalOpen(false)}
      >
        {[
          <div key="canvas" className="share-image-canvas">
            <img className="share-image-cont" src={shareImg} alt="share-img" />
            <p key="slogan" className="share-image-slogan">
              <span>{$t('邀请领主，好友绑定')}</span>
            </p>
            <img className="share-image-logo" src={logo} alt="logo" />
            <div className="invite-code-block">
              <p className="invite-code-text">
                <span>{$t('邀请码')}</span>
              </p>
              <div className="invite-code">{inviteCode}</div>
            </div>
          </div>,
          [
            <p key="title" className="share-image-title">
              <i className="share-image-title__left-flag"></i>
              <span>{$t('分享')}</span>
              <i className="share-image-title__right-flag"></i>
            </p>,
          ],
        ]}
      </ShareImage>
    </div>
  )
}
